import type {
  ErrorResponse,
  CatalogueResponse,
  CrashBetRequest,
  CrashBetResponse,
  CrashCashoutResponse,
  CrashEventsResponse,
  CrashLobbyStateResponse,
  CrashProofResponse,
  EngineAction,
  EngineBet,
  HistoryResponse,
  RoundProofResponse,
  RoundResponse,
  RotateServerSeedResponse,
  SeedCommitmentResponse,
  FunSessionRequest,
  SessionInitResponse,
  StateResponse,
} from '@gp/contracts';

const BASE_URL = import.meta.env.VITE_GAME_API_URL ?? 'http://127.0.0.1:3002';

/** A provider error that reached the client with its documented code intact. */
export class GameApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'GameApiError';
  }
}

export class GameApi {
  private sessionToken: string | null = null;

  private async call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    // Bodyless POST must not advertise application/json — Nest/Fastify rejects
    // empty bodies when that content-type is set.
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (this.sessionToken) headers.authorization = `Bearer ${this.sessionToken}`;

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (error) {
      throw new GameApiError(
        'NETWORK',
        error instanceof Error ? error.message : 'network error',
        0,
      );
    }

    const text = await response.text();
    const parsed: unknown = text ? JSON.parse(text) : {};

    if (!response.ok) {
      const failure = parsed as ErrorResponse;
      throw new GameApiError(
        failure.code ?? 'INTERNAL_ERROR',
        failure.message ?? 'request failed',
        response.status,
      );
    }
    return parsed as T;
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  async openSession(launchToken: string): Promise<SessionInitResponse> {
    const session = await this.call<SessionInitResponse>('POST', '/v1/game/session', {
      launchToken,
    });
    this.sessionToken = session.sessionToken;
    return session;
  }

  /** Play money, no operator. Only answers where the deployment allows it. */
  async openFunSession(request: Partial<FunSessionRequest> = {}): Promise<SessionInitResponse> {
    const session = await this.call<SessionInitResponse>('POST', '/v1/game/fun-session', {
      gameCode: 'dice',
      ...request,
    });
    this.sessionToken = session.sessionToken;
    return session;
  }

  /**
   * Places a bet in any game.
   *
   * What goes in `bet` is the game's business — dice wants a direction and a
   * target, plinko wants rows and a risk — and the client knows it from the
   * JSON Schema the session handed back. Nothing about the shape is checked
   * here, so a new game needs no change to this file.
   */
  bet(gameCode: string, bet: EngineBet, clientRoundId: string): Promise<RoundResponse> {
    return this.call<RoundResponse>('POST', `/v1/game/${gameCode}/bet`, { bet, clientRoundId });
  }

  /** Makes a move in a round that is already open. */
  action(gameCode: string, roundId: string, action: EngineAction): Promise<RoundResponse> {
    return this.call<RoundResponse>('POST', `/v1/game/${gameCode}/action`, { roundId, action });
  }

  /** The round this session left open, or `null` if there is not one. */
  async active(gameCode: string): Promise<RoundResponse | null> {
    const response = await this.call<RoundResponse | { status: 'OK'; round: null }>(
      'GET',
      `/v1/game/${gameCode}/active`,
    );
    return 'roundId' in response ? response : null;
  }

  proof(gameCode: string, roundId: string): Promise<RoundProofResponse> {
    return this.call<RoundProofResponse>('GET', `/v1/game/${gameCode}/proof/${roundId}`);
  }

  // The seed stream belongs to the player, not to a game: rotating it here
  // rotates it for every game they play, which is why these routes carry no
  // game code.

  seedCommitment(): Promise<SeedCommitmentResponse> {
    return this.call<SeedCommitmentResponse>('GET', '/v1/game/seed/commitment');
  }

  setClientSeed(clientSeed: string): Promise<SeedCommitmentResponse> {
    return this.call<SeedCommitmentResponse>('POST', '/v1/game/seed/client-seed', { clientSeed });
  }

  rotateServerSeed(): Promise<RotateServerSeedResponse> {
    return this.call<RotateServerSeedResponse>('POST', '/v1/game/seed/server-seed');
  }

  state(): Promise<StateResponse> {
    return this.call<StateResponse>('GET', '/v1/game/state');
  }

  async catalogue(): Promise<CatalogueResponse> {
    const response = await fetch('/api/v1/admin/games');
    if (!response.ok)
      throw new GameApiError('CATALOGUE_ERROR', 'Failed to load games', response.status);
    const data = await response.json();
    return {
      status: 'OK',
      games: (data.games ?? []).map((g: Record<string, unknown>) => ({
        gameCode: g.code ?? g.gameCode,
        name: g.name,
        type: g.type,
        // How the round is played, which is what decides whether the lobby
        // can offer the game without a scene written for it.
        kind: g.kind ?? 'INSTANT',
        rtp: Number(g.rtp),
        thumbnailUrl: g.thumbnailUrl ?? null,
        tags: g.tags ?? [],
        isNew: g.isNew ?? false,
        isHot: g.isHot ?? false,
        sortOrder: g.sortOrder ?? 0,
        demoAvailable: g.demoAvailable ?? true,
      })),
    };
  }

  async history(page = 1, pageSize = 20): Promise<HistoryResponse> {
    return this.call<HistoryResponse>('GET', `/v1/game/history?page=${page}&pageSize=${pageSize}`);
  }

  // Crash game methods

  crashLobbyState(lobbyId: string): Promise<CrashLobbyStateResponse> {
    return this.call<CrashLobbyStateResponse>('GET', `/v1/game/crash/lobbies/${lobbyId}/state`);
  }

  crashPlaceBet(
    lobbyId: string,
    request: Omit<CrashBetRequest, 'clientEntryId'> & { clientEntryId: string },
  ): Promise<CrashBetResponse> {
    return this.call<CrashBetResponse>('POST', `/v1/game/crash/lobbies/${lobbyId}/bets`, request);
  }

  crashCashout(lobbyId: string, entryId: string): Promise<CrashCashoutResponse> {
    return this.call<CrashCashoutResponse>(
      'POST',
      `/v1/game/crash/lobbies/${lobbyId}/entries/${entryId}/cashout`,
    );
  }

  crashEvents(lobbyId: string, after = 0): Promise<CrashEventsResponse> {
    return this.call<CrashEventsResponse>(
      'GET',
      `/v1/game/crash/lobbies/${lobbyId}/events?after=${after}`,
    );
  }

  crashRoundProof(roundId: string): Promise<CrashProofResponse> {
    return this.call<CrashProofResponse>('GET', `/v1/game/crash/rounds/${roundId}/proof`);
  }
}
